import React, { useEffect, useRef, useState } from "react";
import { Volume2, Upload, Trash2, Play } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * DirectorSoundboard — one-click SFX soundboard for live streams.
 *
 * Fires pre-loaded audio clips into a shared WebAudio destination that the
 * parent (AdvancedDirectorPanel) pipes into the RTP outbound via
 * replaceAudioTrack. Works alongside the music-mixer mic+music graph — the
 * soundboard layer is a third bus with its own GainNode.
 *
 * Props:
 *   - ctx: AudioContext (shared with the mixer)
 *   - dest: MediaStreamAudioDestinationNode (what the broadcaster sends out)
 *   - onFire: optional callback when a clip is fired (for analytics/UI)
 */
export default function DirectorSoundboard({ ctx, dest, onFire, disabled = false }) {
  const [presets, setPresets] = useState([]);
  const [mine, setMine] = useState([]);
  const [vol, setVol] = useState(0.8);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const gainRef = useRef(null);
  const nodeCacheRef = useRef({}); // id -> { audio, source } so re-click replays cleanly

  // ---- load SFX lists ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [pRes, mRes] = await Promise.all([
          api.get("/soundboard/presets"),
          api.get("/soundboard/mine").catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setPresets(pRes.data || []);
        setMine(mRes.data || []);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  // ---- wire the soundboard bus into the mixer ----
  useEffect(() => {
    if (!ctx || !dest) return;
    if (!gainRef.current) {
      const g = ctx.createGain();
      g.gain.value = vol;
      g.connect(dest);
      // Also monitor locally so the director hears it
      try { g.connect(ctx.destination); } catch {}
      gainRef.current = g;
    }
    gainRef.current.gain.value = vol;
  }, [ctx, dest, vol]);

  const fire = async (clip) => {
    if (!ctx || !dest || !gainRef.current) {
      toast.info("Start the mix first — then fire clips to go out on stream");
      return;
    }
    try { await ctx.resume(); } catch {}
    try {
      const audio = new Audio();
      audio.src = clip.url;
      audio.crossOrigin = "anonymous";
      audio.preload = "auto";
      // Play the clip: connect as MediaElementSource into the SFX bus
      await audio.play().catch(() => {});
      try {
        const src = ctx.createMediaElementSource(audio);
        src.connect(gainRef.current);
      } catch {
        // Some browsers don't allow creating a source after play() started —
        // the <audio> will still play locally as a fallback.
      }
      nodeCacheRef.current[clip.id || clip.url] = audio;
      onFire?.(clip);
    } catch (e) {
      toast.error("Could not play clip");
    }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Clip too big — keep soundboard clips under 10MB");
      return;
    }
    setUploading(true);
    try {
      const label = f.name.replace(/\.[^.]+$/, "").slice(0, 60);
      const fd = new FormData();
      fd.append("file", f);
      const qs = `?label=${encodeURIComponent(label)}&emoji=%F0%9F%94%8A`;
      const { data } = await api.post(`/soundboard/upload${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMine((m) => [data, ...m]);
      toast.success("Clip added to your soundboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const removeClip = async (clip) => {
    if (!window.confirm(`Delete "${clip.label}" from your soundboard?`)) return;
    try {
      await api.delete(`/soundboard/${clip.id}`);
      setMine((m) => m.filter((c) => c.id !== clip.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---- Keyboard hot-keys (1-9) — fires the first 9 preset clips so a
  // host (or assistant director) can slam stings without taking their
  // hands off the keyboard. We deliberately ignore key presses while the
  // user is typing in an input/textarea/contenteditable, so chatting in
  // the live room never accidentally triggers a buzzer.
  const fireRef = useRef(fire);
  useEffect(() => { fireRef.current = fire; });
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key >= "1" && e.key <= "9") {
        const idx = parseInt(e.key, 10) - 1;
        const clip = presets[idx];
        if (clip) {
          e.preventDefault();
          fireRef.current?.(clip);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [presets]);

  return (
    <div data-testid="director-soundboard" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-white font-display font-bold text-sm">
          <Volume2 className="w-4 h-4 text-tv-accent" /> Soundboard
        </div>
        <div className="flex items-center gap-2 flex-1 max-w-[140px]">
          <span className="text-[10px] text-white/50 font-bold uppercase">FX</span>
          <input
            type="range"
            data-testid="soundboard-vol"
            min="0"
            max="1.2"
            step="0.05"
            value={vol}
            onChange={(e) => setVol(parseFloat(e.target.value))}
            className="flex-1 accent-tv-accent"
          />
          <span className="text-[10px] text-white/60 tabular-nums w-8 text-right">{Math.round(vol * 100)}%</span>
        </div>
      </div>

      {disabled && (
        <div className="text-[10px] text-tv-accent/80 bg-tv-accent/10 border border-tv-accent/30 rounded px-2 py-1.5">
          Start the music mix first to pipe SFX to viewers. Clips still play locally for you.
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-3 gap-1.5">
        {presets.map((p, idx) => {
          const hotkey = idx < 9 ? String(idx + 1) : null;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => fire(p)}
              data-testid={`soundboard-preset-${p.id}`}
              className="relative bg-white/5 hover:bg-tv-accent/20 hover:text-tv-accent border border-white/10 text-white text-[11px] font-bold px-1.5 py-2 rounded transition flex flex-col items-center gap-0.5"
              title={`${p.label}${hotkey ? ` — press ${hotkey}` : ""}`}
            >
              {hotkey && (
                <span
                  data-testid={`soundboard-hotkey-${p.id}`}
                  className="absolute top-0.5 right-1 text-[8px] font-black uppercase tracking-wider text-tv-accent/80 bg-black/40 rounded px-1 py-0.5 leading-none border border-tv-accent/25"
                >{hotkey}</span>
              )}
              <span className="text-lg leading-none">{p.emoji}</span>
              <span className="truncate w-full text-center">{p.label}</span>
            </button>
          );
        })}
      </div>
      {presets.length > 0 && (
        <div className="text-[9px] text-white/40 uppercase tracking-wider px-1">
          Press <span className="text-tv-accent">1–9</span> on your keyboard to fire the first 9 stings.
        </div>
      )}

      <div className="border-t border-white/10 pt-2 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider font-bold text-white/50">My Clips</div>
          <input
            type="file"
            accept="audio/*"
            ref={fileRef}
            className="hidden"
            onChange={onUpload}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="soundboard-upload-btn"
            className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-60 text-black font-bold text-[10px] px-2 py-1 rounded-full flex items-center gap-1"
          >
            <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload clip"}
          </button>
        </div>
        {mine.length === 0 && (
          <div className="text-[10px] text-white/40 italic">No custom clips yet. Upload short MP3/WAV stings (≤10MB each).</div>
        )}
        <div className="grid grid-cols-2 gap-1">
          {mine.map((c) => (
            <div key={c.id} className="flex items-center gap-1 bg-white/5 rounded px-1.5 py-1 group">
              <button
                type="button"
                onClick={() => fire(c)}
                data-testid={`soundboard-mine-${c.id}`}
                className="flex-1 text-left text-[11px] text-white hover:text-tv-accent truncate flex items-center gap-1 min-w-0"
              >
                <Play className="w-3 h-3 shrink-0" /> <span className="truncate">{c.label}</span>
              </button>
              <button
                type="button"
                onClick={() => removeClip(c)}
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition"
                title="Delete"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
